import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { DataTableCell, DataTableHeader } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Viewpdf from "@MasterBluePrint/Icons/Viewpdf";
import { OrderDocumentsModel } from "@MasterBluePrint/Types/ApiModels";
import React from 'react';
import { connect } from "react-redux";
import { css } from "styled-components";

// Interface for the props of DocumentModal
interface DocumentModalProps {
    isOpen: boolean;
    handleClose: () => void;
    translate: (key: string) => string;
    orderDocuments: OrderDocumentsModel[] | undefined;
    orderNumber: any;
    documentModalStyles: DocumentModalStyles;
}

interface OwnProps {
    order: OrderModel;
}
// Interface for the styles of CancelOrderModalButtonSet
export interface DocumentModalStyles {
    buttonsWrapper?: InjectableCss;
    documentModal?: ModalPresentationProps;
    confirmButtons?: InjectableCss;
    confirmContainer?: InjectableCss;
    cancelButtonCustom?: InjectableCss;
    closeContainer?: InjectableCss;
    confirmMessage?: InjectableCss;
    continueOrderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
}

// Import the cancelOrderModalStyles object
const documentModalStyles: DocumentModalStyles = {
    documentModal: {
        sizeVariant: "small",
    },
    confirmContainer: {
        css: css`
            &.loader-shown {
                &:before {
                    z-index: 1;
                    content: '';
                    background-color: rgba(9,30,66,0.2);
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }
            }
        `,
    },
    confirmButtons: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding-top: 1.625rem;
            margin-top: 1.875rem;
            border-top: 1px solid #D7D7D7;
        `,
    },
    confirmMessage: {
        css: css`
            position: relative;
            h1 {
                color: #002554;
                font-family: 'Roboto', sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 32px;
                position: relative;
                margin-bottom: 2.1875rem;
                margin-top: .5rem;

                &:after {
                    height: 2px;
                    width: 44px;
                    background-color: #C63527;
                    content: '';
                    display: inline-block;
                    position: absolute;
                    bottom: -12px;
                    left: 1px;
                }
            }

            .icon-alert {
                svg {
                    transform: scale(1.5);
                    stroke: #c63527;
                }
            }

            p {
                color: #002554;
                font-family: Lato, sans-serif;
                font-size: .9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
            }
            .spinner-container {
                position: absolute;
                text-align: center;
                margin: 0;
                z-index: 999999;
                display: block;
                left: 43%;
                top: 55%;
            }
            .table-head {
                width: 540px;
                height: 40px;
            }
            .table-header {
                background: #E5EEF7;
                height: 40px;
                border-bottom: none;
            }
            .table-header-content {
                text-align: end;
                font-size: 14px;
            }
            .table-cell {
                text-align: end;
                width: 25%;
                border-bottom: 1px solid #C2D1D9;
            }           
            .document-name{
                word-break:break-word;
                white-space: break-spaces;
                border-bottom: 1px solid #C2D1D9;
                width: 75%;
            }
            .viewPdfIcon{
                margin-left:40px;
            }
        `,
    },
    continueOrderButton: {
        buttonType: "solid",
        css: css`
            border-radius: 2px;
            background-color: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;

            &:hover,
            &:active {
                background-color: #002554;
                border: 2px solid #002554
            }
            span {
                color: #fff;
            }
        `,
    },
    cancelButtonCustom: {
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: .9375rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            cursor: pointer;
            justify-content: center;
            width: auto;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        margin-left: 2em;
                        margin-right: 2em;
                        min-width: 100px;
                    `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    closeContainer: {
        css: css`
            text-align: right;
           .close-label {
                color: #535E64;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
};

const styles = documentModalStyles;
const mapStateToProps = (state: ApplicationState) => ({
    isCancelling: state.pages.orderDetails.isCanceling,
});

type Props = ReturnType<typeof mapStateToProps> & DocumentModalProps;

const DocumentModal: React.FC<Props> = ({ isOpen, handleClose, translate, documentModalStyles, isCancelling, orderDocuments, orderNumber }) => {
    return (
        <Modal
            isOpen={isOpen}
            data-test-selector="documentModal"
            {...styles.documentModal}
            handleClose={handleClose}
            className="confirm-remove-modal"
        >
            <Typography as="div" {...styles.confirmContainer} className={`${isCancelling && isCancelling === true ? "loader-shown" : ""}`}
            >
                <Typography as="div" onClick={handleClose} {...styles.closeContainer}>
                    <span className="close-label">Close</span>
                    <Icon className="icon-close" src="X" />
                </Typography>
                <Typography as="div" {...styles.confirmMessage}>
                    <h1>{translate("PDF Documents for Order#")} {orderNumber} </h1>
                    <DataTable className="table-with-documents">
                        <DataTableHead className="table-head">
                            <DataTableHeader className="table-header">&nbsp;</DataTableHeader>
                            <DataTableHeader className="table-header table-header-content">{translate("View PDF")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {orderDocuments?.map(item => (
                                <DataTableRow key={item.documentName}>
                                    <DataTableCell  className="document-name">{translate(item.documentName || "")}</DataTableCell>
                                    <DataTableCell className="table-cell">
                                        <a
                                            className="viewPdfIcon"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={item.documentUrl}
                                        >
                                            <Icon src={Viewpdf} />
                                        </a>
                                    </DataTableCell>                                   
                                </DataTableRow>   
                            ))}
                        </DataTableBody>                         
                    </DataTable>
                </Typography>
                <Typography as="div" {...styles.confirmButtons}>
                    <Clickable onClick={handleClose} {...styles.cancelButtonCustom}>
                        {translate("cancel")}
                    </Clickable>
                </Typography>
            </Typography>
        </Modal>
    );
};

export default connect(mapStateToProps)(DocumentModal);
